import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Paper, Stack, TextField } from '@mui/material';
import { ProspectLeadsTableHeader } from './components/ProspectLeadsTableHeader';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import ProspectLeadAddEdit from './components/ProspectLeadAddEdit';
import ProspectLeadsBulkUpload from './components/ProspectLeadsBulkUpload';
import AssignButton from './components/AssignButton';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getProspectLeadsTable } from '../../redux/features/globalSlice';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { DeleteButton } from '../../components/mui/Buttons/Buttons';
import LeadStatusUpdate from './components/LeadStatusUpdate';
import LeadTransferUpdate from './components/LeadTransferUpdate';
import LeadAssignUpdate from './components/LeadAssignUpdate';
import LeadViewHisory from './components/LeadViewHisory';
import { prospect_leads_departments } from '../../utils/constants/departmentDetails';

const ProspectLeads = () => {
  const dispatch = useDispatch();
  const profile = JSON.parse(localStorage.getItem('cred')).profile;
  const { departmentId, isHod, isReportingHead } = profile;
  const isDeptOgdOrCsd = prospect_leads_departments.map((item) => item.id).includes(departmentId);
  const { data, meta, loading } = useSelector((state) => state.global.prospectLeadTable);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const type = [
    { id: 1, name: 'All' },
    { id: 2, name: 'My Prospects' },
    { id: 3, name: 'Department Wise' },
  ];
  const [selectedType, setSelectedType] = useState(isDeptOgdOrCsd ? (!isHod || !isReportingHead ? 2 : 3) : 3);
  const isActionDisabled = selectedType == 1 ? true : false;

  const dispatchParams = useMemo(
    () => ({
      page: tableState.page,
      limit: tableState.pageSize,
      search: tableState.search || '',
      type:
        selectedType == 1 ? 'all' : selectedType == 2 ? 'myProspects' : selectedType == 3 ? 'departmentWise' : 'all',
    }),
    [tableState.page, tableState.pageSize, tableState.search, selectedType],
  );

  useEffect(() => {
    dispatch(getProspectLeadsTable(dispatchParams));
  }, [dispatch, dispatchParams]);

  const handleRefreshParams = useMemo(() => ({ ...dispatchParams, hardRefresh: true }), [dispatchParams]);

  const handleRefresh = useCallback(() => {
    dispatch(getProspectLeadsTable(handleRefreshParams));
  }, [dispatch, handleRefreshParams]);

  //refersh logic + close
  const closeModal = useCallback(
    (shouldRefresh = false) => {
      setModalState({ actionKey: '', row: null });

      if (shouldRefresh) {
        handleRefresh();
      }
    },
    [handleRefresh],
  );

  const [modalState, setModalState] = useState({
    actionKey: '',
    row: null,
  });
  const handleButtonActionClick = useCallback((actionKey, row, actionType) => {
    if (!actionKey || !row) return;

    setModalState({
      actionKey,
      row,
    });
  }, []);

  const modalConfigs = useMemo(
    () => [
      {
        actionKey: 'addLead',
        title: 'Add Lead',
        render: () => <ProspectLeadAddEdit onClose={closeModal} />,
      },
      {
        actionKey: 'editLead',
        title: 'Edit Lead',
        render: ({ row }) => <ProspectLeadAddEdit row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'assignLead',
        title: 'Assign Lead',
        render: ({ row }) => (
          <LeadAssignUpdate row={row} checkedRowIds={tableState.checkedRowIds} onClose={closeModal} />
        ),
      },

      {
        actionKey: 'transferLead',
        title: 'Transfer To Department',
        render: ({ row }) => <LeadTransferUpdate row={row} onClose={closeModal} />,
      },
      {
        actionKey: 'statusLead',
        title: 'Update Status',
        render: ({ row }) => <LeadStatusUpdate row={row} onClose={closeModal} />,
      },
      // {
      //   actionKey: 'deleteLead',
      //   title: 'Delete Lead',
      //   render: ({ row }) => (
      //     <DeleteButton action={() => handleDelete(row)} variant="outlined">
      //       Confirm Delete
      //     </DeleteButton>
      //   ),
      // },
      {
        actionKey: 'viewLead',
        title: 'View History',
        render: ({ row }) => <LeadViewHisory row={row} />,
      },
    ],
    [tableState.checkedRowIds, closeModal],
  );

  const activeConfig = useMemo(
    () => modalConfigs.find((config) => config.actionKey === modalState.actionKey),
    [modalConfigs, modalState.actionKey],
  );

  return (
    <GlobalWrapper title={'Prospect Leads'}>
      <Box mt={1}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{
            flexWrap: 'wrap', // Allows wrapping into multiple rows
            rowGap: 2, // Adds vertical space when wrapped
          }}
        >
          {/* 1. Type Select */}
          <TextField
            select
            size="small"
            label="Type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            sx={{
              width: { xs: 'calc(50% - 8px)', md: 180 },
            }}
          >
            {type.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>

          {/* 2. Add Lead Button */}
          <Box sx={{ width: { xs: 'calc(50% - 8px)', md: 'auto' } }}>
            <AssignButton
              type="add"
              label="Add Lead"
              fullWidth
              onClick={() => setModalState({ actionKey: 'addLead', row: null })}
            />
          </Box>

          {/* 3. Bulk Upload */}
          <Box sx={{ width: { xs: 'calc(40% - 8px)', md: 'auto' } }}>
            <ProspectLeadsBulkUpload params={dispatchParams} refreshTable={handleRefresh} />
          </Box>

          {/* 4. Bulk Assign Button */}
          <Box sx={{ width: { xs: 'calc(40% - 8px)', md: 'auto' } }}>
            <AssignButton
              type="assign"
              label="Bulk Assign"
              fullWidth
              disabled={
                isActionDisabled ||
                tableState.checkedRowIds.length === 0 ||
                !isDeptOgdOrCsd ||
                (isDeptOgdOrCsd && !isHod && !isReportingHead)
              }
              onClick={() =>
                setModalState({
                  actionKey: 'assignLead',
                  row: null,
                })
              }
            />
          </Box>
          {/* </Stack> */}
        </Stack>
        <Paper elevation={3}>
          <Box mt={2}>
            <GlobalTable
              rowUniqueId="prospect_lead_id"
              headers={ProspectLeadsTableHeader(isDeptOgdOrCsd, isActionDisabled)}
              tableState={tableState}
              setTableState={setTableState}
              checkboxNeeded={isDeptOgdOrCsd}
              loading={loading}
              rows={data}
              meta={meta}
              onButtonActionClick={handleButtonActionClick}
            />
          </Box>
          <Box>
            <GlobalDialog
              title={activeConfig?.title}
              open={Boolean(modalState?.actionKey)}
              setOpen={(val) => {
                if (!val) setModalState({ actionKey: '', row: null });
              }}
              closeButton={true}
              content={activeConfig?.render ? activeConfig.render({ row: modalState.row }) : null}
              maxWidth={activeConfig?.actionKey === 'viewLead' ? 'lg' : 'md'}
              fullWidth={true}
            />
          </Box>
        </Paper>
      </Box>
    </GlobalWrapper>
  );
};

export default ProspectLeads;
